import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { MenuItem, Switch, TextField } from '@mui/material';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { promoCodesFormFields } from '../../forms/fields/formFields/promoCodes';
import { promoCodeSchema } from '../../forms/validationSchema/formSchema/promoCode';
import { useAppDispatch } from '../../redux/hooks';
import { createPromoCode, updatePromoCode } from '../../redux/modules/prompCodes/promoCode.actions';
import { promoCodeSelector } from '../../redux/modules/prompCodes/promoCode.selectors';
import { PromoCode, PromoCodeTypesEnum } from '../../types/promoCodes';
import { isDispatchApiSuccess } from '../../utils/api';

interface PromoCodeDialogProps {
  open: boolean;
  initialValue: PromoCode | null;
  onClose: (isReload: boolean) => void;
}

export function PromoCodeDialog({ open, initialValue, onClose }: PromoCodeDialogProps) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { handleSubmit, values, handleChange, touched, errors, submitForm, setValues, resetForm } = useFormik({
    initialValues: {
      [promoCodesFormFields.name]: '',
      [promoCodesFormFields.code]: '',
      [promoCodesFormFields.maxUses]: 0,
      [promoCodesFormFields.type]: '',
      [promoCodesFormFields.isActive]: false,
      [promoCodesFormFields.expirationDate]: '',
      // [promoCodesFormFields.associatedPartner]: '',
    },
    validationSchema: promoCodeSchema,
    onSubmit: async (formValues) => {
      if (initialValue?.id) {
        const res = await dispatch(updatePromoCode({ id: initialValue.id, ...formValues }));

        if (isDispatchApiSuccess(res)) {
          onClose(true);
          enqueueSnackbar('Promo code updated!', { variant: 'success' });
        } else {
          enqueueSnackbar('Promo code update error!', { variant: 'error' });
        }
      } else {
        const res = await dispatch(createPromoCode(formValues));

        if (isDispatchApiSuccess(res)) {
          onClose(true);
          enqueueSnackbar('Promo code created!', { variant: 'success' });
        } else {
          enqueueSnackbar('Promo code creation error!', { variant: 'error' });
        }
      }
    },
  });
  const { createStatus, updateStatus, error } = useSelector(promoCodeSelector);
  const isLoading = createStatus === fetchStatuses.pending || updateStatus === fetchStatuses.pending;

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (initialValue && open) {
      setValues({
        [promoCodesFormFields.name]: initialValue.name || '',
        [promoCodesFormFields.code]: initialValue.code || '',
        [promoCodesFormFields.maxUses]: initialValue.maxUses || 0,
        [promoCodesFormFields.type]: initialValue.type || '',
        [promoCodesFormFields.isActive]: initialValue.isActive || false,
        [promoCodesFormFields.expirationDate]: (() => {
          if (!initialValue.expirationDate) return '';
          if (typeof initialValue.expirationDate === 'string') {
            return initialValue.expirationDate;
          }
          return new Date(initialValue.expirationDate).toISOString().slice(0, 10);
        })(),
        // [promoCodesFormFields.associatedPartner]: initialValue.associatedPartner || '',
      });
    }
  }, [initialValue, open, setValues]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>{initialValue?.id ? 'Edit Promo Code' : 'Create a new Promo Code'}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {error && <Alert severity="error">{error.message}</Alert>}
          {/* Enter the Promo Code name */}
        </DialogContentText>
        <form onSubmit={handleSubmit}>
          <TextField
            margin="normal"
            required
            fullWidth
            id={promoCodesFormFields.name}
            name={promoCodesFormFields.name}
            label="Code Name"
            value={values[promoCodesFormFields.name]}
            onChange={handleChange}
            error={touched.name && !!errors.name}
            helperText={touched.name && errors.name}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id={promoCodesFormFields.code}
            name={promoCodesFormFields.code}
            label="Promo Code"
            type="text"
            value={values[promoCodesFormFields.code]}
            onChange={handleChange}
            error={touched.code && !!errors.code}
            helperText={touched.code && errors.code}
            inputProps={{ min: 0 }}
          />
          <TextField
            margin="normal"
            required
            select
            fullWidth
            id={promoCodesFormFields.type}
            name={promoCodesFormFields.type}
            label="Type"
            value={values[promoCodesFormFields.type]}
            onChange={handleChange}
            error={touched.type && !!errors.type}
            helperText={touched.type && errors.type}
          >
            {Object.entries(PromoCodeTypesEnum).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="normal"
            required
            fullWidth
            id={promoCodesFormFields.expirationDate}
            name={promoCodesFormFields.expirationDate}
            label="Expiration Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={values[promoCodesFormFields.expirationDate]}
            onChange={handleChange}
            error={touched.expirationDate && !!errors.expirationDate}
            helperText={touched.expirationDate && errors.expirationDate}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id={promoCodesFormFields.maxUses}
            name={promoCodesFormFields.maxUses}
            label="Max Uses"
            type="number"
            value={values[promoCodesFormFields.maxUses]}
            onChange={handleChange}
            error={touched.maxUses && !!errors.maxUses}
            helperText={touched.maxUses && errors.maxUses}
            inputProps={{ min: 0 }}
          />

          {/* {values[promoCodesFormFields.type] === Object.keys(PromoCodeTypesEnum)[0] && (
            <TextField
              margin="normal"
              fullWidth
              id={promoCodesFormFields.associatedPartner}
              name={promoCodesFormFields.associatedPartner}
              label="Associated Partner"
              value={values[promoCodesFormFields.associatedPartner]}
              onChange={handleChange}
              error={touched.associatedPartner && !!errors.associatedPartner}
              helperText={touched.associatedPartner && errors.associatedPartner}
            />
          )} */}

          {/* Switch for isActive */}
          <div style={{ display: 'flex', alignItems: 'center', marginTop: 16 }}>
            <span style={{ marginRight: 8 }}>Active</span>
            <Switch
              checked={Boolean(values[promoCodesFormFields.isActive])}
              onChange={(e) =>
                handleChange({
                  target: {
                    name: promoCodesFormFields.isActive,
                    value: e.target.checked,
                  },
                })
              }
              name={promoCodesFormFields.isActive}
              color="primary"
              inputProps={{ 'aria-label': 'is active' }}
            />
          </div>
        </form>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          type="button"
          color="primary"
          variant="contained"
          endIcon={<SaveIcon />}
          loading={isLoading}
          loadingPosition="end"
          onClick={submitForm}
        >
          {initialValue?.id ? 'Save' : 'Create'}
        </LoadingButton>
        <Button color="inherit" variant="outlined" onClick={() => onClose(false)}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
